import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowLeft, Pencil, Trash2, Truck, Search } from "lucide-react";
import { useTranslation } from "../../contexts/I18nContext";
import { handleApiError } from "../../utils/errorHandler";
import { usePermissions } from "../../hooks/usePermissions";

const EMPTY_FORM = {
  ragione_sociale: "",
  codice_fiscale: "",
  partita_iva: "",
  indirizzo: "",
  citta: "",
  telefono: "",
  email: "",
  contatto_referente: "",
  note: "",
};

export default function ProduzioneFornitoriPage() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const canEditProduzione = canEdit("produzione");

  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/tenant/produzione/fornitori");
      setFornitori(res.data);
    } catch (e) {
      handleApiError(e, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowDialog(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({
      ragione_sociale: f.ragione_sociale || "",
      codice_fiscale: f.codice_fiscale || "",
      partita_iva: f.partita_iva || "",
      indirizzo: f.indirizzo || "",
      citta: f.citta || "",
      telefono: f.telefono || "",
      email: f.email || "",
      contatto_referente: f.contatto_referente || "",
      note: f.note || "",
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.ragione_sociale.trim()) {
      toast.error("Ragione sociale obbligatoria");
      return;
    }
    try {
      if (editing) {
        await api.put(`/tenant/produzione/fornitori/${editing.id}`, form);
        toast.success("Fornitore aggiornato");
      } else {
        await api.post("/tenant/produzione/fornitori", form);
        toast.success("Fornitore creato");
      }
      setShowDialog(false);
      load();
    } catch (e) {
      handleApiError(e, t("errors.saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Eliminare il fornitore? Sarà rimosso anche dalle commesse collegate.")) return;
    try {
      await api.delete(`/tenant/produzione/fornitori/${id}`);
      toast.success("Fornitore eliminato");
      load();
    } catch (e) {
      handleApiError(e, t("errors.deleteFailed"));
    }
  };

  const filtered = fornitori.filter(f => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (f.ragione_sociale || "").toLowerCase().includes(s) ||
      (f.partita_iva || "").toLowerCase().includes(s) ||
      (f.citta || "").toLowerCase().includes(s) ||
      (f.email || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6" data-testid="produzione-fornitori-page">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${tenant}/produzione`)} data-testid="btn-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Truck className="h-6 w-6" />
              Fornitori Produzione
            </h1>
            <p className="text-muted-foreground">Anagrafica fornitori collegabili alle commesse</p>
          </div>
        </div>
        {canEditProduzione && (
          <Button onClick={openCreate} data-testid="btn-nuovo-fornitore">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Fornitore
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per ragione sociale, P.IVA, città..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-fornitori"
              />
            </div>
            <CardTitle className="ml-auto text-sm text-muted-foreground">
              {filtered.length} fornitori
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Caricamento...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nessun fornitore trovato</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ragione Sociale</TableHead>
                  <TableHead>P.IVA</TableHead>
                  <TableHead>Città</TableHead>
                  <TableHead>Contatto</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(f => (
                  <TableRow key={f.id} data-testid={`fornitore-row-${f.id}`}>
                    <TableCell className="font-medium">{f.ragione_sociale}</TableCell>
                    <TableCell className="font-mono text-xs">{f.partita_iva || "-"}</TableCell>
                    <TableCell>{f.citta || "-"}</TableCell>
                    <TableCell>{f.contatto_referente || "-"}</TableCell>
                    <TableCell className="text-xs">{f.email || "-"}</TableCell>
                    <TableCell>{f.telefono || "-"}</TableCell>
                    <TableCell>
                      {canEditProduzione && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`btn-edit-${f.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(f.id)} data-testid={`btn-delete-${f.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Fornitore" : "Nuovo Fornitore"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2">
              <Label>Ragione Sociale *</Label>
              <Input value={form.ragione_sociale} onChange={e => setForm({ ...form, ragione_sociale: e.target.value })} data-testid="input-ragione-sociale" />
            </div>
            <div className="space-y-2">
              <Label>Partita IVA</Label>
              <Input value={form.partita_iva} onChange={e => setForm({ ...form, partita_iva: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Codice Fiscale</Label>
              <Input value={form.codice_fiscale} onChange={e => setForm({ ...form, codice_fiscale: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Indirizzo</Label>
              <Input value={form.indirizzo} onChange={e => setForm({ ...form, indirizzo: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Città</Label>
              <Input value={form.citta} onChange={e => setForm({ ...form, citta: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Contatto/Referente</Label>
              <Input value={form.contatto_referente} onChange={e => setForm({ ...form, contatto_referente: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Note</Label>
              <Textarea rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSave} data-testid="btn-salva-fornitore">{editing ? "Aggiorna" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
